import React, { useState } from 'react';
// mui components
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
// components
import ErrorModal from 'components/error-modal';
import PurpleButton from 'components/purple-button';
import { StyledEditButton } from 'pages/profile/components/styled-components';
import SectionCard from './section-card';
import { useSpotifyArtistPreview } from './spotify-artist-preview';
// hooks
import { useError } from 'hooks/useError';
import { IArtistProfile } from 'hooks/swrRequests/useArtistProfiles';
// api
import api from 'utils/api';
// config
import { getReviewGroups } from '../steps';
// icons
import { Eye, EyeOff, Lock } from 'lucide-react';
// styles
import { colors } from 'styles/colors';
import { fontStyles } from 'styles/font-styles';
import { useTranslation } from 'react-i18next';

/** Spotify URL row value: artist avatar + name when the URL resolves, raw URL otherwise. Links to the artist page. */
const SpotifyArtistValue: React.FC<{ url: string }> = ({ url }) => {
  const artist = useSpotifyArtistPreview(url);

  if (!artist) {
    return (
      <Typography
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={url}
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: '#141414',
          textAlign: 'right',
          maxWidth: '50%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {url}
      </Typography>
    );
  }
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        maxWidth: '50%',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': { textDecoration: 'underline' },
      }}
      title={url}
    >
      {artist.imageUrl && (
        <Box
          component="img"
          src={artist.imageUrl}
          alt={artist.name}
          sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'cover' }}
        />
      )}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: '#141414',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {artist.name}
      </Typography>
    </Box>
  );
};

/** Banking value masked to its last 4 characters, with an eye toggle to reveal. */
const SensitiveValue: React.FC<{ value: string }> = ({ value }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: '50%', minWidth: 0 }}>
      <IconButton
        size="small"
        aria-label={t(visible ? 'Hide' : 'Show')}
        onClick={() => setVisible((prev) => !prev)}
        sx={{ color: '#8C8C8C' }}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </IconButton>
      <Typography
        title={visible ? value : undefined}
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: '#141414',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {visible ? value : `•••• ${value.slice(-4)}`}
      </Typography>
    </Stack>
  );
};

interface OwnProps {
  profile: IArtistProfile;
  accountEmail?: string;
  /** Sidebar is currently locked — completing this profile unlocks SongFund. */
  locked: boolean;
  onJump: (stepIndex: number) => void;
  onBack: () => void;
  onCompleted: (updated: IArtistProfile) => void;
}

const ReviewStep: React.FC<OwnProps> = ({
  profile,
  accountEmail,
  locked,
  onJump,
  onBack,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { message, setError } = useError();

  const [isSubmitting, setSubmitting] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const groups = getReviewGroups(profile, accountEmail);
  const missingCount = profile.missingFields.length;
  const allDone = missingCount === 0;

  const handleComplete = async () => {
    try {
      setSubmitting(true);
      const updated = await api.artistProfile.complete<IArtistProfile>({ id: profile.id });
      onCompleted(updated);
    } catch (err) {
      setError(err);
      setOpenErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{}}>
      <Stack spacing={2.2}>
        {groups.map((group) => (
          <SectionCard
            key={group.title}
            title={t(group.title)}
            action={
              <StyledEditButton disableRipple onClick={() => onJump(group.stepIndex)}>
                {t('Edit')}
              </StyledEditButton>
            }
          >
            {group.rows.map((row, rowIndex) => (
              <Stack
                key={row.label}
                direction="row"
                justifyContent="space-between"
                sx={{
                  py: 1.1,
                  borderBottom: rowIndex < group.rows.length - 1 ? '1px solid #E9E9E9' : 'none',
                }}
              >
                <Typography sx={{ fontSize: 16, color: '#8C8C8C' }}>{t(row.label)}</Typography>
                {row.key === 'spotifyUrl' && row.value ? (
                  <SpotifyArtistValue url={row.value} />
                ) : row.sensitive && row.value ? (
                  <SensitiveValue value={row.value} />
                ) : (
                  <Typography
                    title={row.value || undefined}
                    sx={{
                      fontSize: 16,
                      fontWeight: 500,
                      color: row.value ? '#141414' : row.optional ? '#8C8C8C' : colors.system.error,
                      textAlign: 'right',
                      // long values preview on one line, cut off with an ellipsis
                      maxWidth: '50%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.value || (row.optional ? '—' : `— ${t('missing')}`)}
                  </Typography>
                )}
              </Stack>
            ))}
          </SectionCard>
        ))}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1.7} sx={{ mt: 3.5 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onBack}
          sx={{
            ...fontStyles.body,
            textTransform: 'none',
            height: '48px',
            borderColor: '#E9E9E9',
            px: 2.7,
          }}
        >
          ← {t('Back')}
        </Button>
        <Typography sx={{ fontSize: 16, color: '#8C8C8C', ml: 'auto !important' }}>
          {profile.completed
            ? ''
            : allDone
            ? t('All set — unlock the platform.')
            : `${missingCount} ${t(missingCount > 1 ? 'fields left' : 'field left')}`}
        </Typography>
        <PurpleButton
          loading={isSubmitting}
          disabled={!allDone || profile.completed}
          onClick={handleComplete}
          sx={{ width: 'auto', px: 3.7, height: '48px' }}
        >
          {profile.completed
            ? t('Saved')
            : locked
            ? t('Save & unlock SongFund')
            : t('Save profile')}
        </PurpleButton>
      </Stack>

      <Stack direction="row" spacing={0.9} alignItems="center" sx={{ mt: 2.7 }}>
        <Lock size={13} color="#B0B0B0" />
        <Typography sx={{ fontSize: 16, color: '#B0B0B0' }}>
          {t(
            'Your identity and payout details are stored securely and used only to process your SongFund payouts.'
          )}
        </Typography>
      </Stack>

      <ErrorModal
        open={openErrorModal}
        handleClose={() => setOpenErrorModal(false)}
        message={message}
      />
    </Box>
  );
};

export default ReviewStep;
